/** @file validateDoc rules for animations: targets, key order, draw-order keys, deform ranges, mix pairs. */
import type { Doc } from './schema';
import type { AnimationDoc } from './schemaParts/animation';
import type { NeedRef } from './refChecker';
import { isSlotPermutation } from './isSlotPermutation';
import { mixNames } from './mixNames';
import { makeError, type Pose2DError } from '../pose2dError';

function checkTargets(anim: AnimationDoc, at: string, need: NeedRef): void {
  for (const id of Object.keys(anim.bones)) need(id, 'bone', `${at}.bones.${id}`);
  for (const id of Object.keys(anim.slots)) need(id, 'slot', `${at}.slots.${id}`);
  for (const id of Object.keys(anim.ik)) need(id, 'ik', `${at}.ik.${id}`);
  for (const id of Object.keys(anim.deform)) need(id, 'attachment', `${at}.deform.${id}`);
  anim.events.forEach((e, i) => { need(e.event, 'event', `${at}.events.${i}.event`); });
}

function checkDrawOrderKeys(doc: Doc, anim: AnimationDoc, at: string, out: Pose2DError[]): void {
  anim.drawOrder.forEach((key, i) => {
    if (key.order === null || isSlotPermutation(doc, key.order)) return;
    out.push(makeError('VALIDATION', {}, { path: `${at}.drawOrder.${i}.order`, hint: 'a draw-order key must list every slot exactly once' }));
  });
}

function checkDeform(doc: Doc, anim: AnimationDoc, at: string, out: Pose2DError[]): void {
  for (const [attId, keys] of Object.entries(anim.deform)) {
    const att = doc.attachments[attId];
    if (!att) continue;
    const path = `${at}.deform.${attId}`;
    if (att.type !== 'mesh') { out.push(makeError('VALIDATION', {}, { path, hint: 'deform keys need a mesh attachment' })); continue; }
    const size = att.uvs.length;
    keys.forEach((key, i) => {
      const o = key.offsets;
      if (!o || (o.start % 2 === 0 && o.values.length % 2 === 0 && o.start + o.values.length <= size)) return;
      out.push(makeError('VALIDATION', {}, { path: `${path}.${i}.offsets`, hint: `start and values.length must be even and start + values.length <= ${size} (2 per vertex)` }));
    });
  }
}

/** Index of the first key whose frame is below the previous one (or equal to it, when `strict`); -1 when in order. */
function strayKey(keys: readonly { f: number }[], strict: boolean): number {
  for (let k = 1; k < keys.length; k++) {
    const f = keys[k]?.f ?? 0;
    const prev = keys[k - 1]?.f ?? 0;
    if (f < prev || (strict && f === prev)) return k;
  }
  return -1;
}

/** VALIDATION at `<path>.<k>` for the first key of `keys` out of frame order (strictly ascending unless `events`). */
function checkKeyList(keys: readonly { f: number }[], path: string, events: boolean, out: Pose2DError[]): void {
  const k = strayKey(keys, !events);
  if (k < 0) return;
  const where = `key ${k} is at frame ${keys[k]?.f ?? 0}, after a key at frame ${keys[k - 1]?.f ?? 0}`;
  const hint = events ? `event ${where}: event keys are stored in frame order` : `${where}: keys are stored in ascending frame order, one per frame`;
  out.push(makeError('VALIDATION', {}, { path: `${path}.${k}`, hint }));
}

/**
 * Every timeline keeps its keys in strictly ascending frame order; event keys in non-descending order (several events,
 * the same one included, may share a frame). The gate behind a key re-inserted at a stale index. O(keys).
 */
function checkKeyOrder(anim: AnimationDoc, at: string, out: Pose2DError[]): void {
  for (const group of ['bones', 'slots', 'ik'] as const) {
    for (const [id, track] of Object.entries(anim[group])) {
      const channels = Object.entries(track as Record<string, { f: number }[] | undefined>);
      for (const [channel, keys] of channels) checkKeyList(keys ?? [], `${at}.${group}.${id}.${channel}`, false, out);
    }
  }
  for (const [attId, keys] of Object.entries(anim.deform)) checkKeyList(keys, `${at}.deform.${attId}`, false, out);
  checkKeyList(anim.drawOrder, `${at}.drawOrder`, false, out);
  checkKeyList(anim.events, `${at}.events`, true, out);
}

function checkMixDefaults(doc: Doc, out: Pose2DError[]): void {
  const known = mixNames(doc.animations);
  for (const pair of Object.keys(doc.mixDefaults ?? {})) {
    for (const name of pair.split('>')) if (!known.has(name)) out.push(makeError('NOT_FOUND', { kind: 'animation', ref: name }, { path: `mixDefaults.${pair}` }));
  }
}

/**
 * Appends errors for timelines on missing entities, keys out of frame order, bad draw-order keys, out-of-range deforms
 * and unknown mix names.
 */
export function checkDocAnimations(doc: Doc, need: NeedRef, out: Pose2DError[]): void {
  for (const [id, anim] of Object.entries(doc.animations)) {
    const at = `animations.${id}`;
    checkTargets(anim, at, need);
    checkKeyOrder(anim, at, out);
    checkDrawOrderKeys(doc, anim, at, out);
    checkDeform(doc, anim, at, out);
  }
  checkMixDefaults(doc, out);
}
